import React, { useEffect, useState, useCallback } from 'react';
import ExamPageShell from '../components/layout/ExamPageShell';
import ProjectCard from '../components/project/ProjectCard';
import NewProjectWizard from '../components/project/NewProjectWizard';
import EmptyState from '../components/common/EmptyState';
import { listExamProjects, deleteExamProject } from '../api/examApi';

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'archived', label: 'Archived' },
];

const IconFolder = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
    <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
  </svg>
);

export default function ExamProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showWizard, setShowWizard] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    listExamProjects(statusFilter || undefined)
      .then((data) => {
        const list = Array.isArray(data) ? data : (data.projects || []);
        list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setProjects(list);
      })
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = projects.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      p.title.toLowerCase().includes(q) ||
      (p.course_name || '').toLowerCase().includes(q) ||
      (p.course_code || '').toLowerCase().includes(q)
    );
  });

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project? This cannot be undone.')) return;
    await deleteExamProject(id).catch(() => {});
    setProjects((ps) => ps.filter((p) => p.id !== id));
  };

  return (
    <ExamPageShell
      breadcrumbs={[{ label: 'Projects' }]}
      headerActions={
        <button className="exam-btn exam-btn-primary exam-btn-md" onClick={() => setShowWizard(true)}>
          + New Project
        </button>
      }
    >
      <div className="exam-page-header">
        <div>
          <h1 className="exam-page-title">Projects</h1>
          <p className="exam-page-subtitle">
            {projects.length} project{projects.length !== 1 ? 's' : ''} in your workspace
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="exam-filter-bar">
        <div className="exam-search-input" style={{ position: 'relative' }}>
          <span className="exam-search-icon" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--exam-text-muted)' }}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14">
              <circle cx="7" cy="7" r="5"/>
              <line x1="11" y1="11" x2="14" y2="14"/>
            </svg>
          </span>
          <input
            className="exam-input"
            style={{ paddingLeft: 32 }}
            placeholder="Search projects…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Status filter pills */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {STATUS_FILTERS.map(({ value, label }) => (
            <button
              key={value}
              className={`exam-btn exam-btn-sm ${statusFilter === value ? 'exam-btn-primary' : 'exam-btn-ghost'}`}
              onClick={() => setStatusFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="exam-spinner" style={{ width: 32, height: 32, borderWidth: 3 }} />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<IconFolder />}
          title={search ? 'No matching projects' : 'No projects yet'}
          body={
            search
              ? 'Try a different search term or clear the filter.'
              : 'Create your first examination project to start building question banks and managing exams.'
          }
          action={
            !search && (
              <button className="exam-btn exam-btn-primary exam-btn-md" onClick={() => setShowWizard(true)}>
                + New Project
              </button>
            )
          }
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {filtered.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onDelete={() => handleDelete(project.id)}
            />
          ))}
        </div>
      )}

      {showWizard && (
        <NewProjectWizard
          onClose={() => setShowWizard(false)}
          onCreated={(p) => {
            setProjects((ps) => [p, ...ps]);
          }}
        />
      )}
    </ExamPageShell>
  );
}
